import { PayPalButtons, PayPalScriptProvider, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { format } from "date-fns";
import { CalendarIcon, CheckCircle2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/storefront/PageHeader";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { useBasket } from "@/context/BasketContext";
import { useSite } from "@/context/SiteContext";
import { API, errMsg, money } from "@/lib/api";

const STEPS = ["Your details", "Review", "Payment"];

function PayPalArea({ order, onApproved }) {
  const [{ isPending, isRejected }] = usePayPalScriptReducer();
  return (
    <div>
      {isPending && (
        <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground" data-testid="paypal-loading">
          <span className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          Loading secure card &amp; PayPal payment…
        </div>
      )}
      {isRejected && (
        <p className="text-sm text-destructive text-center py-2" data-testid="paypal-load-error">
          Online payment couldn't load just now — please place your order below to pay on collection.
        </p>
      )}
      <PayPalButtons
        style={{ layout: "vertical", shape: "pill" }}
        data-testid="paypal-buttons"
        createOrder={async () => {
          const r = await API.post("/paypal/create-order", { order_id: order.id });
          return r.data.paypal_order_id;
        }}
        onApprove={async (data) => {
          try {
            await API.post("/paypal/capture-order", { order_id: order.id, paypal_order_id: data.orderID });
            onApproved();
          } catch (err) {
            toast.error(errMsg(err));
          }
        }}
        onError={() => toast.error("Card payment didn't go through. You can try again, or use the option below to pay on collection.")}
      />
    </div>
  );
}

export default function Checkout() {
  const { items, subtotal, clear } = useBasket();
  const { settings, storeOpen } = useSite();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [order, setOrder] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    delivery_name: "", address: "", postcode: "", date: "", instructions: "", note: "",
  });

  const delivery = settings?.delivery || {};
  const deliveryDays = Array.isArray(delivery.days) && delivery.days.length ? delivery.days.map(Number) : [1, 2, 3, 4, 5, 6];
  const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const deliveryDayNames = [...deliveryDays].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)).map((d) => DAY_NAMES[d]).join(", ");
  const normPc = (s) => String(s || "").replace(/\s+/g, "").toUpperCase();
  const pcRules = Array.isArray(delivery.postcodes) ? delivery.postcodes.map(normPc).filter(Boolean) : [];
  const postcodeBlocked = pcRules.length > 0 && form.postcode.trim() !== "" && !pcRules.some((p) => normPc(form.postcode).startsWith(p));
  const businessPhone = settings?.business?.phone || "01495 247273";
  const fee = useMemo(() => {
    if (delivery.free_delivery_enabled === false) return Number(delivery.fee || 0);
    const freeOver = Number(delivery.free_over) > 0 ? Number(delivery.free_over) : 0;
    return freeOver && subtotal >= freeOver ? 0 : Number(delivery.fee || 0);
  }, [delivery, subtotal]);
  const total = Math.round((subtotal + fee) * 100) / 100;

  useEffect(() => {
    if (items.length === 0 && !order) navigate("/basket", { replace: true });
  }, [items, order, navigate]);

  useEffect(() => {
    if (settings && !storeOpen && !order) navigate("/basket", { replace: true });
  }, [settings, storeOpen, order, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const detailsValid =
    form.name.trim() && /.+@.+\..+/.test(form.email) && form.phone.trim() &&
    form.address.trim() && form.postcode.trim() && !postcodeBlocked;

  const createOrder = async () => {
    setPlacing(true);
    try {
      const payload = {
        items: items.map((it) => ({
          product_id: it.product_id,
          quantity: it.quantity,
          variation: it.variation,
          addons: (it.addons || []).map((a) => a.name),
        })),
        customer: { name: form.name, email: form.email, phone: form.phone },
        delivery: {
          name: form.delivery_name || form.name,
          address: form.address,
          postcode: form.postcode,
          date: form.date,
          instructions: form.instructions,
        },
        note: form.note,
      };
      const r = await API.post("/orders", payload);
      setOrder(r.data);
      setStep(2);
      return r.data;
    } catch (err) {
      toast.error(errMsg(err));
      return null;
    } finally {
      setPlacing(false);
    }
  };

  const finish = (o) => {
    clear();
    navigate(`/order-confirmation/${o.id}?email=${encodeURIComponent(o.customer.email)}`, { replace: true });
  };

  const payLater = async () => {
    if (order) finish(order);
  };

  const fallbackPayLater = async () => {
    if (!order) return;
    try {
      await API.post(`/orders/${order.id}/pay-later`, { email: order.customer.email });
      toast.success("Order placed — we'll be in touch to arrange payment");
    } catch (err) {
      /* order already exists; proceed to confirmation regardless */
    }
    finish(order);
  };

  const paypal = settings?.paypal || {};

  return (
    <div data-testid="checkout-page">
      <Seo title="Checkout | Eileen Jenkins Florist" />
      <PageHeader title="Checkout" testId="checkout-header" />
      <div className="max-w-3xl mx-auto px-4 pb-20">
        <div className="flex items-center gap-2 mb-10" data-testid="checkout-steps">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${i <= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                {i < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`text-sm hidden sm:block ${i <= step ? "font-medium" : "text-muted-foreground"}`}>{s}</span>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border" />}
            </div>
          ))}
        </div>

        {step === 0 && (
          <Card data-testid="checkout-details-form">
            <CardContent className="p-6 sm:p-8 space-y-6">
              <div>
                <h2 className="font-display text-xl mb-4">Your details</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="co-name">Full name *</Label>
                    <Input id="co-name" value={form.name} onChange={set("name")} data-testid="checkout-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="co-email">Email *</Label>
                    <Input id="co-email" type="email" value={form.email} onChange={set("email")} data-testid="checkout-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="co-phone">Phone *</Label>
                    <Input id="co-phone" type="tel" value={form.phone} onChange={set("phone")} data-testid="checkout-phone-input" />
                  </div>
                </div>
              </div>
              <div>
                <h2 className="font-display text-xl mb-4">Delivery details</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="co-dname">Recipient name (if different)</Label>
                    <Input id="co-dname" value={form.delivery_name} onChange={set("delivery_name")} data-testid="checkout-recipient-input" />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="co-address">Delivery address *</Label>
                    <Textarea id="co-address" rows={2} value={form.address} onChange={set("address")} data-testid="checkout-address-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="co-postcode">Postcode *</Label>
                    <Input id="co-postcode" value={form.postcode} onChange={set("postcode")} data-testid="checkout-postcode-input" />
                    {postcodeBlocked && (
                      <p className="text-xs text-destructive" data-testid="checkout-postcode-error">
                        Sorry, we don't deliver to this postcode — please call us on {businessPhone} to arrange, or choose collection.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Preferred delivery date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start font-normal" data-testid="checkout-date-input">
                          <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                          {form.date ? format(new Date(form.date + "T00:00:00"), "EEEE d MMMM yyyy") : "Choose a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={form.date ? new Date(form.date + "T00:00:00") : undefined}
                          onSelect={(d) => d && setForm({ ...form, date: format(d, "yyyy-MM-dd") })}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || !deliveryDays.includes(d.getDay())}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-muted-foreground">We deliver: {deliveryDayNames}</p>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="co-instructions">Delivery instructions</Label>
                    <Input id="co-instructions" placeholder="e.g. leave with neighbour if out" value={form.instructions} onChange={set("instructions")} data-testid="checkout-instructions-input" />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="co-note">Card message / order note</Label>
                    <Textarea id="co-note" rows={3} placeholder="A message for the card, or anything we should know" value={form.note} onChange={set("note")} data-testid="checkout-note-input" />
                  </div>
                </div>
              </div>
              <Button className="rounded-full px-8" size="lg" disabled={!detailsValid} onClick={() => setStep(1)} data-testid="checkout-continue-btn">
                Review order
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 1 && (
          <Card data-testid="checkout-review">
            <CardContent className="p-6 sm:p-8">
              <h2 className="font-display text-xl mb-4">Review your order</h2>
              <div className="space-y-3">
                {items.map((it, i) => {
                  const line = (it.unit_price + (it.addons || []).reduce((s, a) => s + a.price, 0)) * it.quantity;
                  return (
                    <div key={i} className="flex justify-between gap-4 text-sm">
                      <span>{it.name}{it.variation ? ` (${it.variation})` : ""} × {it.quantity}</span>
                      <span className="font-medium">{money(line)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="border-t mt-4 pt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span data-testid="review-subtotal">{money(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span data-testid="review-delivery">{fee === 0 ? "Free" : money(fee)}</span></div>
                <div className="flex justify-between font-semibold text-base"><span>Total</span><span data-testid="review-total">{money(total)}</span></div>
              </div>
              <div className="mt-4 text-sm text-muted-foreground">
                <p><strong className="text-foreground">Deliver to:</strong> {form.delivery_name || form.name}, {form.address}, {form.postcode}{form.date ? ` · ${form.date}` : ""}</p>
                {form.note && <p className="mt-1"><strong className="text-foreground">Note:</strong> {form.note}</p>}
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" className="rounded-full" onClick={() => setStep(0)} data-testid="checkout-back-btn">Back</Button>
                <Button className="rounded-full px-8" size="lg" disabled={placing} onClick={createOrder} data-testid="checkout-place-order-btn">
                  {placing ? "Placing order…" : "Continue to payment"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && order && (
          <Card data-testid="checkout-payment">
            <CardContent className="p-6 sm:p-8">
              <h2 className="font-display text-xl mb-2">Payment</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Order <strong className="text-foreground">{order.order_number}</strong> · Total <strong className="text-foreground">{money(order.total)}</strong>
              </p>
              {paypal.enabled && paypal.client_id ? (
                <>
                  <PayPalScriptProvider options={{ clientId: paypal.client_id, currency: settings?.currency || "GBP", intent: "capture" }}>
                    <PayPalArea order={order} onApproved={() => finish(order)} />
                  </PayPalScriptProvider>
                  <div className="mt-6 pt-6 border-t text-center" data-testid="pay-later-fallback">
                    <p className="text-sm text-muted-foreground mb-3">
                      Having trouble paying online? Place your order now and pay on collection or by phone.
                    </p>
                    <Button variant="outline" className="rounded-full" onClick={fallbackPayLater} data-testid="pay-later-fallback-btn">
                      Place order &amp; pay on collection / by phone
                    </Button>
                    <p className="text-xs text-muted-foreground mt-3">Or call us on {businessPhone}.</p>
                  </div>
                </>
              ) : (
                <div>
                  <p className="text-sm text-muted-foreground mb-6" data-testid="pay-later-note">
                    Pay on delivery or collection — we'll confirm your order shortly.
                  </p>
                  <Button className="rounded-full px-8" size="lg" onClick={payLater} data-testid="confirm-order-btn">
                    Confirm order
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
